import React, { useState, useEffect } from "react";
import { Typography, Box, Tab, Tabs, Button, Modal, Divider } from '@mui/material';
import axios from "axios";
import { createTheme, ThemeProvider } from '@mui/material/styles';
import GeneralInformationBox from "../../Utilities/NCProForms/GeneralInformationBox";
import AffectedItems from "../../Utilities/NCProForms/AffectedItems";
import NonConformanceDescription from "../../Utilities/NCProForms/NonConformanceDescription";
import AdditionalInformation from "../../Utilities/NCProForms/AdditionalInformation";
import PreventiveActionReview from "../../Utilities/NCProForms/PreventiveActionReview";
import CorrectiveActionReview from "../../Utilities/NCProForms/CorrectiveActionReview";
import NDRCEntry from "../../Utilities/NCProForms/NDRCEntry";
import PRCEntry from "../../Utilities/NCProForms/PRCEntry";
import PreventiveActions from "../../Utilities/NCProForms/PreventiveActions";
import Attachments from "../../Utilities/NCProForms/Attachments";
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import SRCEntry from "../../Utilities/NCProForms/SRCEntry";
import IICA from "../../Utilities/NCProForms/IICA";
import ProgressStepper from "../../Utilities/ProgressStepper";
import { useSelector } from "react-redux";
import { ModalClose, Sheet } from "@mui/joy";
import CircularLoader from "../../Common/CircularLoader";
import { convertDBNameToUILabel } from "../../../Helpers/NCRHelpers";
import { reviewSchema } from "../../../Helpers/schema";

const theme = createTheme({
  components: {
    MuiSelect: {
      styleOverrides: {
        select: {
          fontSize: "14px",
          fontWeight: 400,
          color: "rgba(84, 84, 84, 1)",
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        input: {
          "&::placeholder": {
            fontSize: "14px",
            fontWeight: 400,
            color: "rgba(84, 84, 84, 1)",
            opacity: 1,
          },
        },
      },
    },
  },
});

function CustomTabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

export default function ReviewDetailsComponent({ content }) {
  const [tabValue, setTabValue] = useState(0);
  const navigate = useNavigate();
  const accessToken = localStorage.getItem('NCProAccessToken');
  const { userData } = useSelector((state) => state.user);
  const [open, setOpen] = React.useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState();

  useEffect(() => {
    if (tabValue === 1) {
      setOpen(true);
    }
  }, [tabValue]);

  const reviewFormData = {
    corrective_action_review_completed: 'Yes',
    is_corrective_action_review_effective_resolving_issue: 'Yes',
    corrective_action_review_feedback: "",
    corrective_action_review_verification_evidence: "",
    preventive_action_review_implemented: "Yes",
    preventive_action_review_sufficient_prevent_recurrence: "Yes",
    preventive_action_review_feedback: "",
    preventive_action_review_verification_evidence: "",
  }
  const [formData, setFormData] = useState(reviewFormData);

  const handleReviewClick = async (reRoute) => {
    try {
      const filteredFormData = {
        corrective_action_review_completed: formData.corrective_action_review_completed || "Yes",
        is_corrective_action_review_effective_resolving_issue: formData.is_corrective_action_review_effective_resolving_issue || "Yes",
        corrective_action_review_feedback: formData.corrective_action_review_feedback || "",
        preventive_action_review_implemented: formData.preventive_action_review_implemented || "Yes",
        preventive_action_review_sufficient_prevent_recurrence: formData.preventive_action_review_sufficient_prevent_recurrence || "Yes",
        preventive_action_review_feedback: formData.preventive_action_review_feedback || "",
        is_reroute: reRoute || false,
      };

      const requiredFields = ["preventive_action_review_feedback", "corrective_action_review_feedback"];
      let newErrors = {};
      requiredFields.forEach((field) => {
        if (!formData[field] || formData[field] === "") {
          newErrors[field] = `${convertDBNameToUILabel(field)} is required`;
        }
      });
      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
        toast.error("Please fill in all required fields.");
        return;
      }
      const formDataPayload = new FormData();
      Object.keys(filteredFormData).forEach((key) => {
        if (filteredFormData[key] !== null && filteredFormData[key] !== undefined) {
          formDataPayload.append(key, filteredFormData[key]);
        }
      });

      if (formData.corrective_action_review_verification_evidence instanceof File) {
        formDataPayload.append("corrective_action_review_verification_evidence", formData.corrective_action_review_verification_evidence);
      }

      if (formData.preventive_action_review_verification_evidence instanceof File) {
        formDataPayload.append("preventive_action_review_verification_evidence", formData.preventive_action_review_verification_evidence);
      }
      await reviewSchema.parseAsync(filteredFormData);
      
      const response = await axios.put(`${process.env.REACT_APP_BACKEND_BASE_URL}/ncr/review/${formData.ncr_id}`,
        formDataPayload,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${accessToken}`,
          },
        });

      if (response.data.success) {
        toast.success(response.data.message || "Form submitted Successful");
        navigate('/ncpro/review');
      } else {
        toast.error(response.data.message || "Failed to submit");
      }
    } catch (error) {
      console.error('Error during form submission:', error?.response?.data || error?.errors?.[0]?.message || "Something went wrong");
      toast.error(error?.response?.data || error?.errors?.[0]?.message || "Something went wrong");
    }
  }

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleChange = (updatedData) => {
    setFormData((prev) => ({
      ...prev,
      ...updatedData,
    }));
    const field = Object.keys(updatedData)[0];
    setErrors((prev) => ({
      ...prev,
      [field]: false,
    }));
  };

  const getFromData = async () => {
    try {
      setIsLoading(true);
      const accessToken = localStorage.getItem('NCProAccessToken');
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_BASE_URL}/ncr/forms/${content.ncrId}`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      setFormData(response.data.ncrForm);
    } catch (error) {
      console.error('Error fetching ncr:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getFromData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {isLoading ? (
        <CircularLoader label="NCRDetails" />
      ) : (
        <ThemeProvider theme={theme}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 14 }}>
            <Box sx={{ display: 'flex' }}>
              <Box sx={{ pr: '4px' }}>
                <img
                  src="/images/leftArrow.png"
                  alt="Icon"
                  style={{ width: 24, height: 24, marginRight: 4, cursor: 'pointer' }}
                  onClick={() => navigate('/ncpro/review')}
                />
              </Box>
              <Box>
                <Typography
                  sx={{
                    fontFamily: 'Lato',
                    fontSize: '18px',
                    fontWeight: 700,
                  }}
                >
                  {formData.ncr_id} - Review
                </Typography>
              </Box>
            </Box>
            <Box>
              <ProgressStepper status={['completed', 'completed', 'inProgress', 'notStarted']} />
            </Box>
          </Box>
          <Box
            sx={{
              height: '75vh',
              overflow: 'auto',
              border: '1px solid #ccc',
              borderRadius: '8px',
              backgroundColor: '#fff',
              marginTop: '16px',
              '&::-webkit-scrollbar': {
                width: '7px',
                height: '7px',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'rgba(211, 211, 211, 1)',
                borderRadius: '5px',
              }
            }}
          >
            <Box sx={{ width: '100%' }}>
              <Box sx={{
                position: 'sticky',
                top: 0,
                backgroundColor: 'rgba(255, 255, 255, 1)',
                zIndex: 10,
                borderBottom: 1,
                borderColor: 'divider',
              }}>
                <Tabs value={tabValue} onChange={handleTabChange} aria-label="basic tabs example">
                  <Tab
                    label="General information"
                    sx={{
                      fontFamily: 'Lato, sans-serif',
                      fontSize: '14px',
                      fontWeight: tabValue === 0 ? '700' : '400',
                      color: tabValue === 0 ? 'rgba(42, 61, 189, 1) !Important' : 'rgba(0, 0, 0, 1) !Important',
                      lineHeight: '16.8px',
                      textTransform: 'none',
                      py: "20px"
                    }}
                  />
                  <Tab
                    label={formData.corrective_action_required === 'No' ? 'Containment action' : 'Containment action & Root cause'}
                    sx={{
                      fontFamily: 'Lato, sans-serif',
                      fontSize: '14px',
                      fontWeight: tabValue === 1 ? '700' : '400',
                      color: tabValue === 1 ? 'rgba(42, 61, 189, 1) !Important' : 'rgba(0, 0, 0, 1) !Important',
                      lineHeight: '16.8px',
                      textTransform: 'none',
                      py: "20px"
                    }}
                  />
                </Tabs>
              </Box>
              <CustomTabPanel value={tabValue} index={0}>
                <GeneralInformationBox content={{ formData: formData, inputDisable: true }} />
                <AffectedItems content={{ formData: formData, inputDisable: true }} />
                <NonConformanceDescription content={{ formData: formData, inputDisable: true }} />
                <AdditionalInformation content={{ label: "review", formData: formData, inputDisable: true }} />
              </CustomTabPanel>
              <CustomTabPanel value={tabValue} index={1}>
                <IICA content={{ formData: formData, inputDisable: true }} />
                <NDRCEntry content={{ formData: formData, inputDisable: true }} />
                <PRCEntry content={{ formData: formData, inputDisable: true }} />
                <SRCEntry content={{ formData: formData, inputDisable: true }} />
                <PreventiveActions content={{ formData: formData, inputDisable: true }} />
                <Attachments content={{ formData: formData, inputDisable: true }} />
                <CorrectiveActionReview content={{ formData: formData, errors: errors }} onChange={handleChange} />
                <PreventiveActionReview content={{ label: "review", formData: formData, errors: errors }} onChange={handleChange} />
              </CustomTabPanel>
              {userData.roleCode === 'CLTADM' && (
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  px: 3, py: 2,
                  position: 'sticky',
                  bottom: 0,
                  backgroundColor: 'rgba(255, 255, 255, 1)',
                  zIndex: 10,
                  borderRadius: '0px 0px 10px 10px',
                  boxShadow: '0px -1px 4px 0px rgba(0, 0, 0, 0.15)'
                }}>
                  {
                    formData.corrective_action_review_completed === "No" ||
                      formData.is_corrective_action_review_effective_resolving_issue === "No" ||
                      formData.preventive_action_review_implemented === "No" ||
                      formData.preventive_action_review_sufficient_prevent_recurrence === "No"
                      ?
                      <Box sx={{ px: 2, display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                        <Button
                          variant="outlined"
                          color="neutral"
                          disabled={tabValue === 0}
                          sx={{
                            fontFamily: 'Lato, sans-serif',
                            fontSize: '14px',
                            fontWeight: '400',
                            border: '1px solid rgba(14, 41, 102, 1) !important',
                            textTransform: 'none',
                            borderRadius: '10px',
                            color: 'rgba(14, 41, 102, 1) !important',
                            opacity: tabValue === 0 ? '70%' : '100%',
                            py: 1
                          }}
                          onClick={() => handleReviewClick(true)}
                        >
                          Re-route
                        </Button>
                      </Box>
                      :
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                        <Button
                          variant="outlined"
                          color="neutral"
                          disabled={tabValue === 0}
                          sx={{
                            background: 'linear-gradient(270.29deg, #43B0D7 0.24%, #0D2663 99.75%)',
                            textTransform: 'none',
                            fontFamily: 'Lato, sans-serif',
                            fontSize: '14px',
                            fontWeight: '700',
                            color: 'rgba(255, 255, 255, 1) !important',
                            borderRadius: '10px',
                            border: 'none !important',
                            py: 1,
                            px: 4,
                            opacity: tabValue === 0 ? '70%' : '100%',
                          }}
                          onClick={() => handleReviewClick(false)}
                        >
                          Approve
                        </Button>
                      </Box>
                  }
                </Box>
              )}
            </Box>
          </Box>
        </ThemeProvider>
      )}
      {userData.roleCode !== 'CLTADM' && (
        <Box>
          <Modal
            aria-labelledby="modal-title"
            aria-describedby="modal-desc"
            open={open}
            onClose={() => {
              setOpen(false);
              setTabValue(0);
            }}
            sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
          >
            <Sheet
              sx={{ maxWidth: 500, borderRadius: 'md', boxShadow: 'lg' }}
            >
              <ModalClose
                size='lg'
                onClick={() => {
                  setOpen(false);
                  setTabValue(0);
                }}
                sx={{ position: 'absolute', top: 8, right: 8 }}
              />

              <Box sx={{ height: 32 }} />
              <Divider sx={{ mb: 2, mt: 1 }} />
              <Box>
                <Typography className='NCR-awaiting-popup' sx={{ px: 4 }}>
                  Awaiting review of the NCR.
                </Typography>
              </Box>
              <Box sx={{
                borderRadius: '0px 0px 10px 10px',
                boxShadow: '0px -1px 4px 0px #00000026',
                textAlign: 'right',
                px: 3,
                py: 1.5,
                mt: 3,
              }}>
                <Button
                  onClick={() => {
                    setOpen(false);
                    setTabValue(0);
                  }}
                  sx={{
                    fontFamily: 'Lato, sans-serif',
                    background: 'linear-gradient(270.29deg, #43B0D7 0.24%, #0D2663 99.75%)',
                    color: 'white',
                    textTransform: 'none',
                    borderRadius: '7px',
                    px: 3,
                  }}
                >
                  Close
                </Button>
              </Box>
            </Sheet>
          </Modal>
        </Box>
      )}
    </>
  );
}
